import { useState, useEffect, useCallback } from 'react';
import { getWatchlist, type WatchlistEntry } from '@/api/market';

const POLL_MS = 1_000;

/**
 * useRealtimeWatchlist — polls /api/sim/watchlist every 1s.
 *
 * Returns a refresh() callback for immediate re-fetch after add/remove.
 */
export function useRealtimeWatchlist() {
  const [watchlist, setWatchlist] = useState<WatchlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    return getWatchlist()
      .then(d => {
        const newList = d.watchlist || [];
        setWatchlist(prev => {
          if (prev.length !== newList.length) return newList;
          const same = prev.every((item, i) => item.code === newList[i].code && item.name === newList[i].name);
          return same ? prev : newList;
        });
      })
      .catch(() => { /* keep last known value */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().finally(() => { if (!cancelled) setLoading(false); });

    const tick = () => {
      if (document.visibilityState === 'visible') load();
    };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  return { watchlist, loading, refresh: load };
}
